import { Box, DialogActions, DialogContent, DialogTitle } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import CardTraderLogo from 'img/cardtrader-logo.svg'
import { cardtraderAlignmentStart, cardtraderOauthConclude, cardtraderOauthStart } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useCardtraderAlignmentStatus } from 'lib/hooks/useCardtraderAlignmentStatus'
import { useSnackbar } from 'notistack'
import qs from 'query-string'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { SalesChannelsConnector } from '../account/SalesChannelConnector'
import { CardtraderFetchingDialog } from './CardtraderFetchingDialog'
import { CardtraderGameSyncDialog } from './CardtraderGameSyncDialog'
import { CardtraderReviewDialog } from './CardtraderReviewDialog'

interface CardtraderConnectProps {
  startAlignment: boolean
}

export const popupCardTraderWindow = async (
  width: number,
  height: number,
  top: number,
  left: number,
  fetchUser: () => Promise<void>,
  startAlignment: boolean,
  onFetchStarted?: () => void
) => {
  const response = await cardtraderOauthStart()

  const cardtraderUri = response.cardtraderUri
  const redirectUri = response.redirectUri

  const popup = window.open(
    cardtraderUri,
    'popup',
    `width=${width},height=${height},top=${top},left=${left}`
  )!
  const popupInterval = setInterval(async () => {
    try {
      if (popup.location.href.includes(redirectUri)) {
        const params = new URLSearchParams(popup.location.search)
        const code = params.get('code')

        if (code) {
          await cardtraderOauthConclude(code, startAlignment)
          if (startAlignment) onFetchStarted?.()
        }

        popup.close()
        clearInterval(popupInterval)
      }
    } catch (e) {}

    if (popup.closed) {
      clearInterval(popupInterval)
      fetchUser()
    }
  }, 500)
}

export const CardtraderConnect = ({
  startAlignment = false,
}: CardtraderConnectProps): JSX.Element => {
  const { user, fetchUser } = useUser()
  const { t } = useTranslation()
  const { handle } = useErrorHandler()
  const { enqueueSnackbar } = useSnackbar()
  const { pathname, search } = useLocation()
  const history = useHistory()

  const width = 600
  const height = 770
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const left = screenWidth / 2 - width / 2
  const top = screenHeight / 2 - height / 2

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [fetchingDialogOpen, setFetchingDialogOpen] = useState(false)
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [reviewDismissed, setReviewDismissed] = useState(false)

  // The announcement links here with its game, which is what opens the review.
  const requestedGameSync = qs.parse(search).gameSync
  const requestedIdGame = requestedGameSync ? Number(requestedGameSync) : undefined
  const [gameSyncOpen, setGameSyncOpen] = useState(false)
  // Held apart from the address, which is cleared as soon as the link is acted on.
  const [syncGame, setSyncGame] = useState<number | undefined>(undefined)

  const ctUser = user?.assignedCardtraderUser
  const isConnected = !!ctUser
  // With the alignment already finished, lastAlignmentEnd is enough: no poll.
  const alignmentFinished = !!ctUser?.lastAlignmentEnd
  const {
    status: liveStatus,
    idGame,
    fetchedAt,
    staleItems,
    skippedListings,
    refresh,
  } = useCardtraderAlignmentStatus(isConnected && !alignmentFinished)
  // A game alignment moves the same fields but belongs to its own flow: this
  // box must not offer its confirm, which imports the whole stock.
  const status = alignmentFinished || idGame !== null ? 'completed' : liveStatus

  useEffect(() => {
    if (status === 'ready_for_review' && !reviewDismissed) {
      setReviewDialogOpen(true)
    }
  }, [status, reviewDismissed])

  // The announcement link reaches whoever reads the changelog, connected or not.
  // Opening the dialog for someone without an account only starts a request the
  // server refuses, so they are told what is missing instead.
  //
  // The link says it once. Left in the address it speaks again on every later
  // render — changing game from the sidebar reopened the dialog to be told that
  // game had already been imported — so it is cleared as soon as it is acted on.
  useEffect(() => {
    if (requestedIdGame === undefined || !user) return

    const remaining = new URLSearchParams(search)
    remaining.delete('gameSync')
    history.replace({ pathname, search: remaining.toString() })

    if (isConnected) {
      setSyncGame(requestedIdGame)
      setGameSyncOpen(true)

      return
    }

    enqueueSnackbar(t('connectCardtrader.gameSync.notConnected'), { variant: 'warning' })
    // t, enqueueSnackbar and history are rebuilt on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedIdGame, isConnected, user, pathname, search])

  // Whoever arrives from the snackbar has no game in the address: the status
  // names it, and the review has to be here waiting for them.
  useEffect(() => {
    if (idGame !== null) setGameSyncOpen(true)
  }, [idGame])

  // Nothing else closes the fetching dialog, and it would keep claiming the
  // download is running.
  useEffect(() => {
    if (status !== null && status !== 'fetching') {
      setFetchingDialogOpen(false)
    }
  }, [status])

  // The server reports a failure once, to whichever poll catches it.
  useEffect(() => {
    if (status === 'failed') {
      enqueueSnackbar(t('connectCardtrader.salesChannelSync.snackbar.failed'), { variant: 'error' })
    }
  }, [status, enqueueSnackbar, t])

  if (!user) return <></>

  const handleSyncNow = async () => {
    try {
      await cardtraderAlignmentStart()
      setReviewDismissed(false)
      setFetchingDialogOpen(true)
      await refresh()
    } catch (error) {
      handle(error)
    }
  }

  const buildConnectedButtons = (): [ReactNode, (e: any) => void][] => {
    if (status === null || status === 'fetching') {
      // SalesChannelsConnector wants an onClick per button: refresh() at least
      // forces a status poll outside the 5s cycle.
      return [
        [
          <span key="fetching" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="fa-regular fa-spinner fa-spin" />
            <span>{t('connectCardtrader.salesChannelSync.fetchInProgress')}</span>
          </span>,
          () => refresh(),
        ],
      ]
    }
    if (status === 'ready_for_review') {
      // Closing the review does not resolve it, so the way back in stays.
      return [
        [
          <span
            key="review"
            data-testid="cardtrader-review-sync"
            style={{ textDecoration: 'underline' }}
          >
            {t('connectCardtrader.salesChannelSync.reviewSync')}
          </span>,
          () => {
            setReviewDismissed(false)
            setReviewDialogOpen(true)
          },
        ],
      ]
    }
    if (status === 'cancelled' || status === 'failed') {
      return [
        [
          <span
            key="sync"
            data-testid="cardtrader-sync-now"
            style={{ textDecoration: 'underline' }}
          >
            {t('connectCardtrader.salesChannelSync.syncNow')}
          </span>,
          handleSyncNow,
        ],
      ]
    }
    return []
  }

  const userConnected = () => {
    return (
      <div>
        <div className="d-flex flex-wrap w-100">
          <SalesChannelsConnector
            type="CardTrader"
            title={user.assignedCardtraderUser!.cardtraderUserName || 'Account'}
            active={true}
            imgIcon={CardTraderLogo}
            buttons={buildConnectedButtons()}
          />
        </div>
      </div>
    )
  }

  const userNotConnected = () => {
    return (
      <SalesChannelsConnector
        type="CardTrader"
        onBoxClick={() => setConfirmOpen(true)}
        title={t('connectCardtrader.connectButton')}
        imgIcon={CardTraderLogo}
        active={false}
        buttons={[]}
      />
    )
  }

  return (
    <Box padding={0}>
      {isConnected ? userConnected() : userNotConnected()}
      <ClosableDialog
        maxWidth={'sm'}
        open={confirmOpen}
        hideButton
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle disableTypography style={{ textAlign: 'center' }}>
          <h3>{t('connectCardtrader.confirmDialog.title')}</h3>
        </DialogTitle>
        <Box display="flex" justifyContent="center" mt={1} mb={1}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: 32, color: '#666' }} />
        </Box>
        <DialogContent>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <li>{t('connectCardtrader.confirmDialog.removeItems')}</li>
            <li>{t('connectCardtrader.confirmDialog.overnightSync')}</li>
          </ul>
        </DialogContent>
        <Box height="1rem" />
        <DialogActions>
          <div className="w-100 d-flex justify-content-evenly">
            <Button variant="outlined" color="primary" onClick={() => setConfirmOpen(false)}>
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                try {
                  popupCardTraderWindow(width, height, top, left, fetchUser, startAlignment, () => {
                    setReviewDismissed(false)
                    setFetchingDialogOpen(true)
                    refresh()
                  })
                } catch (error) {
                  handle(error as Error)
                }
                setConfirmOpen(false)
              }}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </DialogActions>
      </ClosableDialog>
      {/* Mounted only when it is needed: the dialog indexes the whole card data,
          and paying that on every visit to Sales Channels slows the page down
          for everyone who is not importing a game. */}
      {gameSyncOpen && (
        <CardtraderGameSyncDialog
          open
          requestedIdGame={syncGame}
          onClose={() => setGameSyncOpen(false)}
        />
      )}
      <CardtraderFetchingDialog
        open={fetchingDialogOpen}
        onClose={() => setFetchingDialogOpen(false)}
      />
      <CardtraderReviewDialog
        open={reviewDialogOpen}
        fetchedAt={fetchedAt}
        staleItems={staleItems}
        skippedListings={skippedListings}
        onClose={() => {
          setReviewDialogOpen(false)
          setReviewDismissed(true)
        }}
        onResolved={() => {
          fetchUser()
          refresh()
        }}
      />
    </Box>
  )
}
